'use client';

import type { PropsWithChildren } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * Context for tracking if descendant tooltips should render immediately
 * in relation to the triggering of another tooltip.
 */

type TooltipContextType = {
  showingId: string | null;
  isCoolingDown: boolean;
  setShowingId: (id: string | null) => void;
};

export const TooltipSubscriptions = React.createContext<TooltipContextType>({
  showingId: null,
  isCoolingDown: false,
  setShowingId: () => {},
});

type TooltipSubscriptionsProviderProps = PropsWithChildren<{
  /**
   * The cooldown delay. If a tooltip hides, and then another is triggered
   * within this delay, the new tooltip will show immediately and
   * without an animation. To remove this behavior, set to 0.
   */
  delay?: number;
}>;

export function TooltipSubscriptionsProvider({
  children,
  delay = 700,
}: TooltipSubscriptionsProviderProps) {
  const [isCoolingDown, setIsCoolingDown] = useState(false);
  const [showingId, setShowingId] = useState<string | null>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const context = useMemo(() => {
    return {
      showingId,
      isCoolingDown,
      setShowingId: (id: string | null) => {
        if (id) {
          setShowingId(id);
          return;
        }

        clearTimeout(cooldownTimerRef.current);
        flushSync(() => {
          setIsCoolingDown(true);
        });

        cooldownTimerRef.current = setTimeout(() => {
          setIsCoolingDown(false);
        }, delay);
      },
    };
  }, [showingId, isCoolingDown, delay]);

  useEffect(() => {
    return () => {
      clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  return (
    <TooltipSubscriptions.Provider value={context}>
      {children}
    </TooltipSubscriptions.Provider>
  );
}

/**
 * Access tooltip global state
 */

export function useTooltipContext() {
  return useContext(TooltipSubscriptions);
}
